const PRICE_TABLE = {
  classic: [999, 799, 599, 549, 499, 479, 449],
  premium: [1499, 1199, 999, 849, 749, 699, 649],
};
const TIER_STARTS = [1, 3, 6, 10, 15, 20, 30];
const DELIVERY_RULES = [
  { label: 'Northern Territory', min: 800, max: 999, plenty: 25, close: 12 },
  { label: 'NSW / ACT', min: 2000, max: 2999, plenty: 18, close: 8 },
  { label: 'Victoria', min: 3000, max: 3999, plenty: 15, close: 6 },
  { label: 'Queensland', min: 4000, max: 4999, plenty: 20, close: 9 },
  { label: 'South Australia', min: 5000, max: 5999, plenty: 18, close: 8 },
  { label: 'Western Australia', min: 6000, max: 6999, plenty: 22, close: 10 },
  { label: 'Tasmania', min: 7000, max: 7999, plenty: 20, close: 9 },
];
const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const DATE = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'long' });
const POSTCODE_STORAGE_PREFIX = 'stepmojo-party-pack:';
const POSTCODE_LATEST_KEY = `${POSTCODE_STORAGE_PREFIX}latest-postcode`;
const POSTCODE_TTL_MS = 24 * 60 * 60 * 1000;

export function tierIndex(quantity) {
  if (quantity <= 2) return 0;
  if (quantity <= 5) return 1;
  if (quantity <= 9) return 2;
  if (quantity <= 14) return 3;
  if (quantity <= 19) return 4;
  if (quantity <= 29) return 5;
  return 6;
}
export function unitPriceCents(level, quantity) {
  const normalized = level === 'premium' ? 'premium' : 'classic';
  return PRICE_TABLE[normalized][tierIndex(Math.max(1, Number(quantity) || 1))];
}
export function nextTierGuidance(level, quantity) {
  const total = Math.max(1, Math.round(Number(quantity) || 1));
  const nextStart = TIER_STARTS[tierIndex(total) + 1] || null;
  return {
    currentUnitPriceCents: unitPriceCents(level, total),
    nextStart,
    giftsToNext: nextStart ? nextStart - total : 0,
  };
}
export function deliveryRuleForPostcode(postcode) {
  const value = String(postcode || '').trim();
  if (!/^\d{4}$/.test(value)) return null;
  const numeric = Number(value);
  return DELIVERY_RULES.find((rule) => numeric >= rule.min && numeric <= rule.max) || null;
}
function localDateFromIso(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.valueOf()) ? null : date;
}
export function deliveryTiming(partyDate, postcode, now = new Date()) {
  const rule = deliveryRuleForPostcode(postcode);
  const date = localDateFromIso(partyDate);
  if (!rule || !date) return { status: 'incomplete', days: null, rule, message: 'Add a valid party date and Australian delivery postcode.' };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.ceil((date - today) / 86400000);
  if (days < 0) return { status: 'invalid-date', days, rule, message: 'Choose a party date that has not passed.' };
  if (days >= rule.plenty) return { status: 'plenty', days, rule, message: `Plenty of time — ${rule.label} standard-delivery planning window.` };
  if (days >= rule.close) return { status: 'close', days, rule, message: 'Getting close — allow extra time and review delivery at checkout.' };
  return { status: 'too-soon', days, rule, message: 'Too soon for standard delivery — contact us before ordering.' };
}
export function distributeBestVariety(quantity, keys) {
  const list = Array.isArray(keys) ? keys.filter(Boolean) : [];
  const total = Math.max(0, Math.round(Number(quantity) || 0));
  if (!list.length) return {};
  const base = Math.floor(total / list.length);
  let remainder = total % list.length;
  return Object.fromEntries(list.map((key) => {
    const value = base + (remainder > 0 ? 1 : 0);
    remainder = Math.max(0, remainder - 1);
    return [key, value];
  }));
}
export function mixSelectionState(required, items) {
  const needed = Math.max(0, Math.round(Number(required) || 0));
  const selected = (Array.isArray(items) ? items : []).reduce((sum, item) => sum + Math.max(0, Math.round(Number(item.quantity) || 0)), 0);
  return { needed, selected, remaining: needed - selected, valid: needed > 0 && selected === needed };
}
export function normalizeExtraQuantity(value, maxQuantity = 60) {
  return clamp(value, 0, Math.max(0, Number(maxQuantity) || 0));
}
export function totalGuestGiftQuantity(guestCount, extraQuantity) {
  return Math.max(0, Math.round(Number(guestCount) || 0)) + Math.max(0, Math.round(Number(extraQuantity) || 0));
}
export function birthdayHeroEligible(giftQuantity) {
  return Math.max(0, Math.round(Number(giftQuantity) || 0)) >= 10;
}
export function spareGiftBoxCount(giftQuantity) {
  const quantity = Math.max(0, Math.round(Number(giftQuantity) || 0));
  if (quantity >= 30) return 5;
  if (quantity >= 20) return 3;
  if (quantity >= 10) return 2;
  return 0;
}
export function birthdayHeroSummary(giftQuantity, included, name = '') {
  if (!birthdayHeroEligible(giftQuantity)) return 'Complimentary with 10+ guest gifts';
  if (!included) return 'Not selected';
  return `1 Complimentary Hero Pack${name ? ` · ${name}` : ''}`;
}
export function summaryMixLabels(_variety, items) {
  const labels = (Array.isArray(items) ? items : [])
    .filter((item) => Math.max(0, Math.round(Number(item.quantity) || 0)) > 0)
    .map((item) => `${Math.max(0, Math.round(Number(item.quantity) || 0))} ${item.title}`);
  return labels.length > 0 ? labels : ['Not selected yet'];
}
export function partyPackConfigurationId(timestamp = Date.now(), random = Math.random()) {
  const timePart = Math.max(0, Math.round(Number(timestamp) || 0)).toString(36);
  const randomPart = Math.floor(Math.max(0, Math.min(0.999999999, Number(random) || 0)) * 2176782336)
    .toString(36)
    .padStart(6, '0');
  return `party-${timePart}-${randomPart}`;
}
function validPostcodeRecord(value, now = Date.now(), requireFresh = false) {
  let record = value;
  if (typeof value === 'string') {
    try {
      record = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!record || !/^\d{4}$/.test(String(record.postcode || ''))) return null;
  const savedAt = Number(record.savedAt) || 0;
  if (requireFresh && (!savedAt || now - savedAt > POSTCODE_TTL_MS || savedAt > now + 60000)) return null;
  return { postcode: String(record.postcode), configurationId: String(record.configurationId || ''), savedAt };
}
function browserStorage(name) {
  if (typeof window === 'undefined') return null;
  try {
    return window[name] || null;
  } catch {
    return null;
  }
}
function readStoredRecord(storage, key, now, requireFresh) {
  if (!storage) return null;
  try {
    const record = validPostcodeRecord(storage.getItem(key), now, requireFresh);
    if (!record && storage.getItem(key)) storage.removeItem(key);
    return record;
  } catch {
    return null;
  }
}
export function storePartyPackPostcode(configurationId, postcode, now = Date.now()) {
  const normalizedId = String(configurationId || '').trim();
  const record = validPostcodeRecord({ postcode, configurationId: normalizedId, savedAt: now }, now);
  if (!normalizedId || !record) return false;
  const serialized = JSON.stringify(record);
  const session = browserStorage('sessionStorage');
  const local = browserStorage('localStorage');
  try { session?.setItem(`${POSTCODE_STORAGE_PREFIX}${normalizedId}`, serialized); } catch {}
  try {
    local?.setItem(`${POSTCODE_STORAGE_PREFIX}${normalizedId}`, serialized);
    local?.setItem(POSTCODE_LATEST_KEY, serialized);
  } catch {}
  return true;
}
export function readPartyPackPostcode(configurationId, { allowLatest = false, now = Date.now() } = {}) {
  const normalizedId = String(configurationId || '').trim();
  if (!normalizedId) return '';
  const key = `${POSTCODE_STORAGE_PREFIX}${normalizedId}`;
  const session = browserStorage('sessionStorage');
  const local = browserStorage('localStorage');
  const sessionRecord = readStoredRecord(session, key, now, false);
  if (sessionRecord) return sessionRecord.postcode;
  const localRecord = readStoredRecord(local, key, now, true);
  if (localRecord) {
    try { session?.setItem(key, JSON.stringify(localRecord)); } catch {}
    return localRecord.postcode;
  }
  if (!allowLatest) return '';
  return readStoredRecord(local, POSTCODE_LATEST_KEY, now, true)?.postcode || '';
}
export function clearPartyPackPostcode(configurationId, now = Date.now()) {
  const normalizedId = String(configurationId || '').trim();
  if (!normalizedId) return;
  const key = `${POSTCODE_STORAGE_PREFIX}${normalizedId}`;
  const session = browserStorage('sessionStorage');
  const local = browserStorage('localStorage');
  try { session?.removeItem(key); } catch {}
  try {
    local?.removeItem(key);
    const latest = readStoredRecord(local, POSTCODE_LATEST_KEY, now, true);
    if (latest?.configurationId === normalizedId) local?.removeItem(POSTCODE_LATEST_KEY);
  } catch {}
}
export function cartItemsForPlan(plan) {
  const properties = {
    '_stepmojo_party_pack': 'custom-mix-v2',
    '_stepmojo_configuration_id': plan.configurationId || '',
    '_stepmojo_party_date': plan.partyDate,
    '_stepmojo_guest_count': String(Math.max(1, Math.round(Number(plan.guestCount) || 1))),
    '_stepmojo_extra_quantity': String(Math.max(0, Math.round(Number(plan.extraQuantity) || 0))),
    '_stepmojo_gift_level': plan.level,
    '_stepmojo_variety': plan.variety,
  };
  const items = plan.mixItems.filter((item) => Math.round(Number(item.quantity)) > 0 && item.variantId).map((item) => ({
    id: Number(item.variantId),
    quantity: Math.max(1, Math.round(Number(item.quantity) || 1)),
    properties: { ...properties, '_stepmojo_collection': item.key },
  }));
  if (plan.birthdayHero?.included && plan.birthdayHero.variantId) {
    items.push({
      id: Number(plan.birthdayHero.variantId),
      quantity: 1,
      properties: {
        ...properties,
        '_stepmojo_birthday_hero': 'complimentary',
        'Birthday Hero first name': plan.birthdayHero.name,
        'Favourite Tiny Nature design': plan.birthdayHero.design,
        '3D birthday card': plan.birthdayHero.card || 'No card selected',
      },
    });
  }
  return items;
}

const PARTY_CART_PROPERTY_KEYS = {
  partyPack: '_stepmojo_party_pack',
  configurationId: '_stepmojo_configuration_id',
  partyDate: '_stepmojo_party_date',
  guestCount: '_stepmojo_guest_count',
  extraQuantity: '_stepmojo_extra_quantity',
  level: '_stepmojo_gift_level',
  variety: '_stepmojo_variety',
  collection: '_stepmojo_collection',
  birthdayHero: '_stepmojo_birthday_hero',
  birthdayName: 'Birthday Hero first name',
  birthdayDesign: 'Favourite Tiny Nature design',
  birthdayCard: '3D birthday card',
};

function partyCartProperty(line, logicalKey) {
  const properties = line?.properties || {};
  return String(properties[logicalKey] ?? properties[PARTY_CART_PROPERTY_KEYS[logicalKey]] ?? '').trim();
}

export function partyCartLineKeysForConfiguration(lines, configurationId) {
  const expected = String(configurationId || '').trim();
  if (!expected) return [];
  return (Array.isArray(lines) ? lines : [])
    .filter((line) => partyCartProperty(line, 'configurationId') === expected)
    .map((line) => line.key)
    .filter(Boolean);
}

export function partyCartItemSignature(line) {
  const variantId = String(line?.variant_id ?? line?.variantId ?? line?.id ?? '').trim();
  const quantity = Math.max(0, Math.round(Number(line?.quantity) || 0));
  return JSON.stringify([
    variantId,
    quantity,
    ...Object.keys(PARTY_CART_PROPERTY_KEYS).map((key) => partyCartProperty(line, key)),
  ]);
}

export function verifyPartyCartItems(lines, desiredItems, configurationId) {
  const expectedId = String(configurationId || '').trim();
  const desired = Array.isArray(desiredItems) ? desiredItems : [];
  const actual = (Array.isArray(lines) ? lines : [])
    .filter((line) => partyCartProperty(line, 'configurationId') === expectedId);
  const expectedSignatures = desired.map(partyCartItemSignature).sort();
  const actualSignatures = actual.map(partyCartItemSignature).sort();
  const valid = Boolean(expectedId)
    && desired.length > 0
    && expectedSignatures.length === actualSignatures.length
    && expectedSignatures.every((signature, index) => signature === actualSignatures[index])
    && actual.every((line) => Boolean(line.key));
  return {
    valid,
    lineKeys: actual.map((line) => line.key).filter(Boolean),
    expectedCount: desired.length,
    actualCount: actual.length,
    message: valid
      ? ''
      : 'Shopify Cart did not preserve the exact Party Pack products, quantities and order details.',
  };
}
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, Math.round(Number(value) || min)));
}
const BaseHTMLElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class StepMojoPartyPackBuilder extends BaseHTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.form = this.querySelector('form');
    this.steps = [...this.querySelectorAll('[data-builder-step]')];
    this.progressItems = [...this.querySelectorAll('[data-progress-step]')];
    this.progressElement = this.querySelector('.stepmojo-party-pack-builder__progress');
    this.pageReference = this.dataset.pageReference === 'true';
    this.mixItems = [...this.querySelectorAll('[data-mix-item]')];
    this.currentStep = 1;

    this.cartBusy = false;
    this.maxQuantity = Number(this.dataset.maxQuantity) || 60;
    const partySize = typeof window === 'undefined' ? null : new URL(window.location.href).searchParams.get('party_size');
    const guestInput = this.querySelector('[data-guest-count]');
    if (guestInput && partySize) guestInput.value = clamp(partySize, 1, this.maxQuantity);
    const dateInput = this.querySelector('[data-party-date]');
    if (dateInput) dateInput.min = new Date().toISOString().slice(0, 10);
    this.addEventListener('click', (event) => this.handleClick(event));
    this.form?.addEventListener('input', () => this.update());
    this.form?.addEventListener('change', () => this.update());
    this.form?.addEventListener('submit', (event) => event.preventDefault());
    this.update();
    this.showStep(1, false);
  }
  handleClick(event) {
    const button = event.target.closest('button');
    const action = button?.dataset.action;
    if (!button || !this.contains(button) || !action) return;
    if (action === 'next') {
      if (this.validateStep(this.currentStep)) this.showStep(Math.min(6, this.currentStep + 1));
      return;
    }
    if (action === 'back') {
      this.showStep(Math.max(1, this.currentStep - 1));
      return;
    }
    if (action === 'review') {
      if (this.validateAll()) this.showStep(6);
      return;
    }
    if (action === 'cart') {
      this.addPlanToCart();
      return;
    }
    if (action === 'summary-toggle') {
      const expanded = this.dataset.summaryExpanded !== 'true';
      this.dataset.summaryExpanded = String(expanded);
      button.setAttribute('aria-expanded', String(expanded));
      this.setText('[data-summary-toggle-label]', expanded ? 'Hide details' : 'View details');
      return;
    }
    const countInput = this.querySelector('[data-guest-count]');
    if (action === 'guest-decrement' || action === 'guest-increment') {
      const adjustment = action === 'guest-increment' ? 1 : -1;
      countInput.value = clamp(Number(countInput.value) + adjustment, 1, this.maxQuantity);
    }
    if (action === 'guest-preset') countInput.value = clamp(button.dataset.value, 1, this.maxQuantity);

    if (action === 'mix-minus' || action === 'mix-plus') {
      const input = button.closest('[data-mix-item]')?.querySelector('[data-mix-quantity]');
      if (input) input.value = Math.max(0, Number(input.value) + (action === 'mix-plus' ? 1 : -1));
    }
    this.update();
  }
  progressStageForBuilderStep(step) {
    if (!this.pageReference) return step;
    if (step <= 2) return step;
    if (step <= 4) return 3;
    if (step === 5) return 4;
    return 5;
  }
  setProgressStage(stage) {
    this.progressItems.forEach((item) => {
      const itemStep = Number(item.dataset.progressStep);
      item.dataset.status = itemStep === stage ? 'current' : itemStep < stage ? 'complete' : 'upcoming';
      if (itemStep === stage) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    });
  }
  showStep(step, focus = true) {
    this.currentStep = step;
    this.dataset.currentStep = String(step);
    const configurationView = this.pageReference && step < 6;
    this.steps.forEach((panel) => {
      const panelStep = Number(panel.dataset.builderStep);
      panel.hidden = this.pageReference
        ? configurationView ? panelStep === 6 : panelStep !== 6
        : panelStep !== step;
    });
    const progressStage = this.progressStageForBuilderStep(step);
    this.setProgressStage(progressStage);
    if (this.pageReference && configurationView) this.updateReferenceProgress(this.state());
    if (!focus) return;
    const panel = this.steps.find((item) => Number(item.dataset.builderStep) === step);
    const currentProgressItem = this.progressItems.find((item) => Number(item.dataset.progressStep) === progressStage);
    const reducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    panel?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    panel?.querySelector('h2')?.focus({ preventScroll: true });
    if (currentProgressItem && this.progressElement?.scrollWidth > this.progressElement.clientWidth) {
      const targetLeft = currentProgressItem.offsetLeft - ((this.progressElement.clientWidth - currentProgressItem.offsetWidth) / 2);
      this.progressElement.scrollTo({ left: Math.max(0, targetLeft), behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  }
  state() {
    const guestCount = clamp(this.querySelector('[data-guest-count]')?.value, 1, this.maxQuantity);
    const extraInput = this.querySelector('[data-extra-count]');
    const extraQuantity = normalizeExtraQuantity(extraInput?.value, this.maxQuantity);
    if (extraInput) extraInput.value = extraQuantity;
    const giftQuantity = totalGuestGiftQuantity(guestCount, extraQuantity);
    const level = this.querySelector('[name="party_level"]:checked')?.value === 'premium' ? 'premium' : 'classic';
    const variety = 'specific';
    const activeItems = this.mixItems.filter((item) => item.dataset.level === level);
    const mixItems = activeItems.map((item) => {
      const input = item.querySelector('[data-mix-quantity]');
      const quantity = Math.max(0, Math.round(Number(input?.value) || 0));
      if (input) input.value = quantity;
      return {
        key: item.dataset.key,
        title: item.dataset.title,
        imageUrl: item.dataset.imageUrl || '',
        quantity,
        variantId: item.dataset.variantId,
        mappingReady: item.dataset.mappingReady === 'true',
      };
    });
    const activeDesignOptions = [...this.querySelectorAll('[data-birthday-design-option]')]
      .filter((option) => option.dataset.level === level);
    if (!activeDesignOptions.some((option) => option.querySelector('input:checked'))) {
      const firstActiveDesign = activeDesignOptions[0]?.querySelector('input');
      if (firstActiveDesign) firstActiveDesign.checked = true;
    }
    const birthdayEligible = birthdayHeroEligible(giftQuantity);
    const birthdayIncluded = birthdayEligible && Boolean(this.querySelector('[data-birthday-include]')?.checked);
    const delivery = deliveryTiming(this.querySelector('[data-party-date]')?.value, this.querySelector('[data-party-postcode]')?.value);
    return {
      guestCount,
      extraQuantity,
      giftQuantity,
      level,
      variety,
      mixItems,
      mix: mixSelectionState(giftQuantity, mixItems),
      partyDate: this.querySelector('[data-party-date]')?.value || '',
      postcode: this.querySelector('[data-party-postcode]')?.value || '',
      delivery,
      birthdayHero: {
        eligible: birthdayEligible,
        included: birthdayIncluded,
        name: this.querySelector('[data-birthday-name]')?.value.trim() || '',
        design: activeDesignOptions.find((option) => option.querySelector('input:checked'))?.querySelector('input')?.value || '',
        card: this.querySelector('[name="birthday_card"]:checked')?.value || '',
        variantId: this.dataset.birthdayVariantId || '',
        mappingReady: this.dataset.birthdayMappingReady === 'true' && Number(this.dataset.birthdayVariantPrice) === 0,
      },
    };
  }
  update() {
    const state = this.state();
    this.querySelectorAll('[data-guest-value]').forEach((node) => { node.textContent = state.guestCount; });
    this.querySelectorAll('[data-gift-total]').forEach((node) => { node.textContent = state.giftQuantity; });
    const extraInput = this.querySelector('[data-extra-count]');
    if (extraInput) extraInput.max = String(Math.max(0, this.maxQuantity - state.guestCount));
    this.querySelectorAll('[data-guest-preset]').forEach((button) => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.value) === state.guestCount));
    });
    this.updateDelivery(state);
    this.updatePricing(state);
    this.updateMix(state);
    this.updateBirthdayHero(state);
    this.updateSummary(state);
    this.updateReadiness(state);
    this.updateReferenceProgress(state);
    this.dispatchPlanEvent(state);
  }
  updateReferenceProgress(state) {
    if (!this.pageReference || this.currentStep === 6) return;
    let stage = 1;
    const deliveryReady = !['incomplete', 'invalid-date', 'too-soon'].includes(state.delivery.status);
    if (deliveryReady) stage = 2;
    if (deliveryReady && state.guestCount >= 1) stage = 3;
    if (deliveryReady && state.mix.valid) stage = 4;
    const birthdayReady = !state.birthdayHero.included || (state.birthdayHero.name && state.birthdayHero.design);
    if (deliveryReady && state.mix.valid && birthdayReady) stage = 5;
    this.setProgressStage(stage);
  }
  updateDelivery(state) {
    const status = this.querySelector('[data-delivery-status]');
    if (status) {
      status.dataset.status = state.delivery.status;
      status.textContent = state.delivery.message;
    }
  }
  updatePricing(state) {
    const guidance = nextTierGuidance(state.level, state.giftQuantity);
    const total = guidance.currentUnitPriceCents * state.giftQuantity;
    this.querySelectorAll('[data-selected-level]').forEach((node) => { node.textContent = state.level === 'premium' ? 'Premium' : 'Classic'; });
    this.querySelectorAll('[data-level-card]').forEach((card) => {
      const cardUnitPrice = unitPriceCents(card.dataset.level, state.giftQuantity);
      card.dataset.selected = String(card.dataset.level === state.level);
      const unitTarget = card.querySelector('[data-unit-price]');
      const totalTarget = card.querySelector('[data-estimated-total]');
      if (unitTarget) unitTarget.textContent = AUD.format(cardUnitPrice / 100);
      if (totalTarget) totalTarget.textContent = AUD.format((cardUnitPrice * state.giftQuantity) / 100);
    });
    const tier = this.querySelector('[data-tier-guidance]');
    if (tier) tier.textContent = guidance.nextStart
      ? `Add ${guidance.giftsToNext} more gift${guidance.giftsToNext === 1 ? '' : 's'} to reach the next price tier.`
      : 'You have reached the final listed quantity tier.';
  }
  updateMix(state) {
    this.mixItems.forEach((item) => {
      item.hidden = item.dataset.level !== state.level;
      item.dataset.selected = String(Number(item.querySelector('[data-mix-quantity]')?.value) > 0);
    });
    const controls = this.querySelector('[data-specific-mix]');
    if (controls) controls.hidden = false;
    const status = this.querySelector('[data-mix-status]');
    if (status) {
      status.dataset.status = state.mix.valid ? 'complete' : state.mix.remaining < 0 ? 'over' : 'remaining';
      status.textContent = state.mix.valid
        ? `${state.mix.needed} needed · ${state.mix.selected} selected ✓`
        : state.mix.remaining >= 0
          ? `${state.mix.needed} needed · ${state.mix.selected} selected · ${state.mix.remaining} remaining`
          : `${state.mix.needed} needed · ${state.mix.selected} selected · remove ${Math.abs(state.mix.remaining)}`;
    }
  }
  updateBirthdayHero(state) {
    const input = this.querySelector('[data-birthday-include]');
    if (input) input.disabled = !state.birthdayHero.eligible;
    this.querySelectorAll('[data-birthday-unlocked]').forEach((unlocked) => { unlocked.hidden = !state.birthdayHero.eligible; });
    this.querySelectorAll('[data-birthday-locked]').forEach((locked) => {
      locked.hidden = state.birthdayHero.eligible;
      const needed = Math.max(0, 10 - state.giftQuantity);
      locked.textContent = `Add ${needed} more guest gift${needed === 1 ? '' : 's'} to unlock a complimentary Birthday Hero Pack.`;
    });
    const details = this.querySelector('[data-birthday-details]');
    if (details) details.hidden = !state.birthdayHero.included;
    this.querySelectorAll('[data-birthday-design-option]').forEach((option) => { option.hidden = option.dataset.level !== state.level; });
  }
  updateSummary(state) {
    const party = this.querySelector('[data-summary-party]');
    if (party) party.textContent = state.partyDate && state.postcode
      ? `${DATE.format(localDateFromIso(state.partyDate))} · ${state.postcode}`
      : 'Add date and postcode';
    this.setText('[data-summary-guests]', String(state.giftQuantity));
    this.setText('[data-summary-level]', state.level === 'premium' ? 'Premium' : 'Classic');
    const unitPrice = unitPriceCents(state.level, state.giftQuantity);
    this.setText('[data-summary-total]', AUD.format((unitPrice * state.giftQuantity) / 100));
    this.setText('[data-summary-total-compact]', AUD.format((unitPrice * state.giftQuantity) / 100));
    this.setText('[data-summary-unit-price]', `${AUD.format(unitPrice / 100)} per guest gift`);
    const spareBoxes = spareGiftBoxCount(state.giftQuantity);
    this.setText('[data-summary-spare-boxes]', spareBoxes
      ? `${spareBoxes} included — just in case`
      : 'Available from 10 guest gifts');
    this.setText('[data-summary-birthday]', birthdayHeroSummary(
      state.giftQuantity,
      state.birthdayHero.included,
      state.birthdayHero.name,
    ));
    const list = this.querySelector('[data-summary-mix]');
    if (list) {
      const summaryLabels = summaryMixLabels(state.variety, state.mixItems);
      const selectedItems = state.mixItems.filter((item) => item.quantity > 0);
      if (!selectedItems.length) {
        const emptyItem = document.createElement('li');
        emptyItem.className = 'stepmojo-build-page-reference__summary-empty';
        emptyItem.textContent = summaryLabels[0];
        list.replaceChildren(emptyItem);
      } else {
        list.replaceChildren(...selectedItems.map((item) => {
          const listItem = document.createElement('li');
          listItem.className = 'stepmojo-build-page-reference__summary-item';
          if (item.imageUrl) {
            const image = document.createElement('img');
            image.src = item.imageUrl;
            image.alt = '';
            image.loading = 'lazy';
            image.width = 56;
            image.height = 56;
            listItem.append(image);
          }
          const copy = document.createElement('span');
          const title = document.createElement('strong');
          title.textContent = item.title;
          const quantity = document.createElement('small');
          quantity.textContent = item.quantity + (item.quantity === 1 ? ' gift' : ' gifts');
          copy.append(title, quantity);
          const total = document.createElement('em');
          total.textContent = AUD.format((unitPrice * item.quantity) / 100);
          listItem.append(copy, total);
          return listItem;
        }));
      }
    }
  }
  updateReadiness(state) {
    const missing = state.mixItems.filter((item) => item.quantity > 0 && (!item.mappingReady || !item.variantId));
    const birthdayReady = !state.birthdayHero.included || state.birthdayHero.mappingReady;
    const customerReady = state.mix.valid
      && state.giftQuantity <= this.maxQuantity
      && !['incomplete', 'invalid-date', 'too-soon'].includes(state.delivery.status)
      && (!state.birthdayHero.included || (state.birthdayHero.name && state.birthdayHero.design));
    const ready = customerReady && !missing.length && birthdayReady && this.dataset.checkoutQaVerified === 'true';
    const button = this.querySelector('[data-cart-button]');
    if (button) button.disabled = !ready || this.cartBusy;
    const status = this.querySelector('[data-cart-status]');
    if (!status) return;
    status.dataset.status = ready ? 'ready' : 'blocked';
    if (state.delivery.status === 'too-soon') status.textContent = 'Please contact us before ordering because standard delivery is too close.';
    else if (!customerReady) status.textContent = 'Complete the required party, mix and Birthday Hero details before adding the pack.';
    else if (missing.length) status.textContent = `Checkout mapping required for: ${missing.map((item) => item.title).join(', ')}.`;
    else if (!birthdayReady) status.textContent = 'The complimentary Birthday Hero product must be mapped to an available $0 Shopify variant.';
    else if (this.dataset.checkoutQaVerified !== 'true') status.textContent = 'Unpublished cart and checkout QA must be verified in the Theme Editor before this button is enabled.';
    else status.textContent = 'Your mapped Party Pack is ready to add to the Shopify cart.';
  }
  validateStep(step) {
    const state = this.state();
    let message = '';
    if (step === 1 && ['incomplete', 'invalid-date'].includes(state.delivery.status)) message = state.delivery.message;
    if (step === 3 && state.giftQuantity > this.maxQuantity) message = `Choose no more than ${this.maxQuantity} total guest gifts.`;
    if (step === 4 && !state.mix.valid) message = 'The selected collection quantities must equal the total number of guest gifts.';
    if (step === 5 && state.birthdayHero.included && (!state.birthdayHero.name || !state.birthdayHero.design)) {
      message = 'Add the Birthday Hero first name and favourite Tiny Nature design.';
    }
    const panel = this.steps.find((item) => Number(item.dataset.builderStep) === step);
    panel?.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
    if (message && step === 1) {
      if (!state.partyDate || state.delivery.status === 'invalid-date') this.querySelector('[data-party-date]')?.setAttribute('aria-invalid', 'true');
      if (!deliveryRuleForPostcode(state.postcode)) this.querySelector('[data-party-postcode]')?.setAttribute('aria-invalid', 'true');
    }
    if (message && step === 5 && !state.birthdayHero.name) {
      this.querySelector('[data-birthday-name]')?.setAttribute('aria-invalid', 'true');
    }
    const status = this.querySelector(`[data-step-error="${step}"]`);
    if (status) status.textContent = message;
    return !message;
  }
  validateAll() {
    for (const step of [1, 2, 3, 4, 5]) {
      if (!this.validateStep(step)) {
        this.showStep(step);
        return false;
      }
    }
    return true;
  }
  async cartRequest(url, body, { method = 'POST' } = {}) {
    const options = {
      method,
      credentials: 'same-origin',
      cache: method === 'GET' ? 'no-store' : 'no-cache',
      headers: { Accept: 'application/json' },
    };
    if (body !== undefined) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    const response = await fetch(url, options);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Shopify Cart could not be updated.');
    }
    return payload;
  }
  async cartSnapshot() {
    return this.cartRequest(this.dataset.cartJsonUrl, undefined, { method: 'GET' });
  }
  async rollbackConfiguration(configurationId) {
    try {
      let snapshot = await this.cartSnapshot();
      let keys = partyCartLineKeysForConfiguration(snapshot.items, configurationId);
      if (!keys.length) return true;
      snapshot = await this.cartRequest(this.dataset.cartUpdateUrl, {
        updates: Object.fromEntries(keys.map((key) => [key, 0])),
      });
      keys = Array.isArray(snapshot.items)
        ? partyCartLineKeysForConfiguration(snapshot.items, configurationId) : ['unverified'];
      if (!keys.length) return true;
      snapshot = await this.cartSnapshot();
      return partyCartLineKeysForConfiguration(snapshot.items, configurationId).length === 0;
    } catch {
      return false;
    }
  }
  async addPlanToCart() {
    if (this.cartBusy || !this.validateAll()) return;
    const state = this.state();
    const configurationId = partyPackConfigurationId();
    const desiredItems = cartItemsForPlan({ ...state, configurationId });
    this.updateReadiness(state);
    const button = this.querySelector('[data-cart-button]');
    if (!button || button.disabled) return;
    if (!desiredItems.length) {
      this.setText('[data-cart-status]', 'Choose at least one mapped Party Pack item before adding it to Cart.');
      return;
    }
    this.cartBusy = true;
    button.disabled = true;
    button.textContent = 'Adding Party Pack…';
    this.setText('[data-cart-status]', 'Adding your mapped Party Pack to Shopify cart…');
    let addAttempted = false;
    try {
      addAttempted = true;
      const added = await this.cartRequest(this.dataset.cartAddUrl, { items: desiredItems });
      let verification = verifyPartyCartItems(added.items, desiredItems, configurationId);
      if (!verification.valid) {
        const snapshot = await this.cartSnapshot();
        verification = verifyPartyCartItems(snapshot.items, desiredItems, configurationId);
      }
      if (!verification.valid) {
        throw new Error(verification.message);
      }
      storePartyPackPostcode(configurationId, state.postcode);
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      const rollbackVerified = !addAttempted || await this.rollbackConfiguration(configurationId);
      this.cartBusy = false;
      button.textContent = 'Add Party Pack to Cart';
      this.setText(
        '[data-cart-status]',
        rollbackVerified
          ? error.message || 'Shopify could not add this Party Pack. No incomplete Party Pack was kept.'
          : 'Shopify could not verify or fully roll back this Party Pack. Open Cart and review its items before trying again.',
      );
      this.updateReadiness(this.state());
    }
  }
  dispatchPlanEvent(state) {
    this.dispatchEvent(new CustomEvent('stepmojo:party-plan-change', {
      bubbles: true,
      detail: {
        expectedGuestCount: state.guestCount,
        extraGiftQuantity: state.extraQuantity,
        completeGiftQuantity: state.giftQuantity,
        giftLevel: state.level,
        variety: state.variety,
        deliveryStatus: state.delivery.status,
        birthdayHeroIncluded: state.birthdayHero.included,
        mix: state.mixItems.map(({ key, quantity }) => ({ key, quantity })),
      },
    }));
  }
  setText(selector, value) {
    const target = this.querySelector(selector);
    if (target) target.textContent = value;
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('stepmojo-party-pack-builder')) {
  customElements.define('stepmojo-party-pack-builder', StepMojoPartyPackBuilder);
}
